import { useCallback, useEffect, useRef, useState } from "react";
import {
    Upload,
    Clock,
    Loader2,
    AlertTriangle,
    Trash2,
    Layers,
    FileText,
    CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import { Button } from "./ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import ScriptRevisionDiffDialog from "./ScriptRevisionDiffDialog";
import ScriptRevisionKindPicker from "./ScriptRevisionKindPicker";

/**
 * Iter-60: Owner-only panel listing pending revisions for an active
 * script + an Upload Revision affordance. Inline under any active
 * script row (works for both feature-film and tv-series surfaces).
 *
 * Iter-128 — Trust pass.
 *  - Upload Revision now forces an explicit choice between Full Script
 *    vs Revised Pages BEFORE the file picker opens, so we never
 *    silently treat revised pages as a complete replacement.
 *  - Each pending revision exposes an explicit Delete affordance so
 *    accidental uploads can be removed before approval — no "stuck"
 *    revisions waiting in the queue.
 */
export default function ScriptRevisionPanel({
    projectId,
    scriptId,
    canRevise = true,
    compact = false,
}) {
    const apiBase = useApiBase();
    const [revisions, setRevisions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [openRevId, setOpenRevId] = useState(null);
    const [openRevLabel, setOpenRevLabel] = useState("");
    const [openRevKind, setOpenRevKind] = useState(null);
    const [kindPickerOpen, setKindPickerOpen] = useState(false);
    const [pendingKind, setPendingKind] = useState(null);
    const [confirmDeleteRev, setConfirmDeleteRev] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const fileRef = useRef(null);

    const refresh = useCallback(async () => {
        if (!scriptId) {
            setRevisions([]);
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/scripts/${scriptId}/revisions`,
            );
            setRevisions(data || []);
        } catch (err) {
            const msg = formatApiError(err);
            if (!String(msg).toLowerCase().includes("not found")) {
                toast.error(msg);
            }
        } finally {
            setLoading(false);
        }
    }, [projectId, scriptId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const startUpload = () => {
        setKindPickerOpen(true);
    };

    const onKindPicked = (kind) => {
        setPendingKind(kind);
        setKindPickerOpen(false);
        // Open the file dialog on the next tick so the modal has time
        // to close (iOS Safari sometimes drops the click otherwise).
        setTimeout(() => {
            if (fileRef.current) fileRef.current.click();
        }, 60);
    };

    // Iter-157P.18 — trustworthy revision upload state machine. The
    // previous flow was a single `api.post` with no client-side
    // timeout and no fallback polling. When the Cloudflare/origin
    // edge timed out (the producer's recurring complaint), the
    // axios promise rejected with a generic network error, a
    // disappearing toast flashed by, and the page never updated.
    // The producer had to manually refresh to discover the
    // revision had in fact uploaded.
    //
    // New design:
    //   * Persistent inline banner shows phase ("Uploading…",
    //     "Processing script…", "Revision ready for review", "We
    //     couldn't process this revision").
    //   * Generous client-side timeout (120s) so we wait past the
    //     typical Cloudflare ~100s edge cap when possible.
    //   * If the request fails BEFORE we got a response, poll the
    //     revisions list every 3s for up to 5 minutes; if a fresh
    //     revision appears with the uploaded filename, treat it as
    //     success and transition to "ready for review".
    //   * On real failure (timeout exhausted with no revision
    //     visible), show plain-English error + Close button. Never
    //     leak Cloudflare/edge wording.
    //
    // `uploadState` shape:
    //   { phase: "uploading" | "processing" | "ready" | "failed" | "stalled",
    //     filename, kind, error, revision_id, started_at }
    const [uploadState, setUploadState] = useState(null);
    // Stable ref so the polling loop can be cancelled if the user
    // closes the banner without leaving the page.
    const pollAbortRef = useRef({ cancelled: false });

    const beginUpload = ({ filename, kind }) => {
        // Reset any previous abort and snapshot a fresh ref for
        // this upload's polling loop.
        pollAbortRef.current = { cancelled: false };
        setUploadState({
            phase: "uploading",
            filename,
            kind,
            error: null,
            revision_id: null,
            started_at: Date.now(),
        });
    };

    const dismissUploadBanner = () => {
        pollAbortRef.current.cancelled = true;
        setUploadState(null);
    };

    const pollForRevision = useCallback(
        async ({ filename, kind, knownRevIds, signal }) => {
            // Poll the revisions endpoint for up to ~5 minutes (5s ×
            // 60 attempts) looking for either:
            //   (a) a brand-new revision whose filename matches
            //   (b) a revision with id not present in `knownRevIds`
            //       (catches filename normalisation differences)
            // Returns the recovered revision, or null on timeout.
            const MAX_ATTEMPTS = 60;
            const POLL_MS = 5000;
            for (let i = 0; i < MAX_ATTEMPTS; i++) {
                if (signal.cancelled) return null;
                try {
                    const { data } = await api.get(
                        `${apiBase}/${projectId}/scripts/${scriptId}/revisions`,
                    );
                    const list = Array.isArray(data) ? data : [];
                    // Match by filename first (most reliable), then
                    // fall back to "any revision id we didn't know
                    // about before the upload started".
                    const byFilename = list.find(
                        (r) => (r.filename || "") === filename,
                    );
                    const byNew = list.find(
                        (r) => !knownRevIds.has(r.id),
                    );
                    const recovered = byFilename || byNew;
                    if (recovered) {
                        return recovered;
                    }
                } catch {
                    /* swallow — keep polling */
                }
                await new Promise((resolve) => setTimeout(resolve, POLL_MS));
            }
            return null;
        },
        [projectId, scriptId],
    );

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !pendingKind) {
            setPendingKind(null);
            return;
        }
        setUploading(true);
        const kind = pendingKind;
        beginUpload({ filename: file.name, kind });
        // Snapshot the current set of known revision ids so the
        // polling loop can detect the new arrival even if filename
        // normalisation differs.
        const knownRevIds = new Set((revisions || []).map((r) => r.id));
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("revision_kind", kind);
            const { data } = await api.post(
                `${apiBase}/${projectId}/scripts/${scriptId}/revisions`,
                fd,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    // Iter-157P.18 — long timeout so we wait past
                    // typical Cloudflare edge windows when the
                    // origin is actually working. If the edge cuts
                    // us short anyway, the catch block hands off to
                    // the polling bridge so the producer never
                    // sees a raw timeout.
                    timeout: 120000,
                },
            );
            const recovered = data?.revision;
            setUploadState({
                phase: "ready",
                filename: file.name,
                kind,
                error: null,
                revision_id: recovered?.id || null,
                started_at: Date.now(),
            });
            await refresh();
        } catch (err) {
            // The request itself failed (edge timeout, 502, 504,
            // or genuine origin error). Transition into polling
            // before declaring failure. The producer sees
            // "Processing script… this may take a moment." until
            // the revision either appears (success) or polling
            // exhausts (failure).
            setUploadState((prev) => ({
                ...(prev || {}),
                phase: "processing",
                filename: file.name,
                kind,
            }));
            const signal = pollAbortRef.current;
            const recovered = await pollForRevision({
                filename: file.name,
                kind,
                knownRevIds,
                signal,
            });
            if (signal.cancelled) {
                // User closed the banner manually — leave state alone.
            } else if (recovered) {
                setUploadState({
                    phase: "ready",
                    filename: file.name,
                    kind,
                    error: null,
                    revision_id: recovered.id,
                    started_at: Date.now(),
                });
                await refresh();
            } else {
                // True failure after the full polling window. The
                // producer gets a plain-English message — we never
                // surface Cloudflare/edge wording.
                setUploadState({
                    phase: "failed",
                    filename: file.name,
                    kind,
                    error:
                        "We couldn't process this revision. Your file may be too large or in an unsupported format. Please try uploading it again from Scripts.",
                    revision_id: null,
                    started_at: Date.now(),
                });
            }
        } finally {
            setUploading(false);
            setPendingKind(null);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const deleteRevision = async (rev) => {
        if (!rev) return;
        setDeleting(true);
        try {
            await api.delete(
                `${apiBase}/${projectId}/scripts/${rev.id}`,
            );
            toast.success("Revision removed");
            setConfirmDeleteRev(null);
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeleting(false);
        }
    };

    if (!canRevise || !scriptId) return null;

    return (
        <div
            data-testid={`script-revision-panel-${scriptId}`}
            className={`rounded-xl border border-white/10 bg-secondary/30 p-3 space-y-2 ${
                compact ? "" : "mt-3"
            }`}
        >
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 flex items-center gap-1.5">
                    <Clock size={11} />
                    Script Changes
                    {(() => {
                        const pendingCount = revisions.filter(
                            (r) => (r.status || "pending_revision") === "pending_revision",
                        ).length;
                        return pendingCount > 0 ? (
                            <span
                                data-testid={`script-revision-pending-count-${scriptId}`}
                                className="ml-1 rounded-full bg-amber-500/20 text-amber-200 px-1.5 text-[10px] font-bold"
                            >
                                {pendingCount}
                            </span>
                        ) : null;
                    })()}
                </h3>
                <input
                    ref={fileRef}
                    type="file"
                    data-testid={`script-revision-file-input-${scriptId}`}
                    accept=".pdf,.docx,.txt,.fountain"
                    className="hidden"
                    onChange={onFile}
                />
                <Button
                    type="button"
                    data-testid={`script-revision-upload-${scriptId}`}
                    variant="secondary"
                    onClick={startUpload}
                    disabled={uploading}
                    className="h-8 px-2 text-xs"
                >
                    {uploading ? (
                        <Loader2 size={12} className="animate-spin" />
                    ) : (
                        <>
                            <Upload size={12} className="mr-1.5" />
                            Upload changes
                        </>
                    )}
                </Button>
            </div>

            <p
                data-testid={`script-revision-helper-${scriptId}`}
                className="text-[11px] text-neutral-400 leading-relaxed"
            >
                Upload an updated script, or just the pages that changed.
                We&apos;ll show you exactly what&apos;s different and you
                decide what to apply. Photos, notes and looks always stay
                with their scenes — nothing is ever deleted.
            </p>

            {uploadState ? (() => {
                // Iter-157P.18 — persistent in-panel banner. The
                // banner stays visible across phase transitions
                // (uploading → processing → ready / failed) so the
                // producer always knows what is happening, even on
                // mobile where transient toasts get missed.
                const us = uploadState;
                const isPages = us.kind === "revised_pages";
                if (us.phase === "uploading" || us.phase === "processing") {
                    return (
                        <div
                            data-testid={`script-revision-upload-banner-${scriptId}`}
                            data-phase={us.phase}
                            className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 flex items-start gap-3"
                        >
                            <Loader2 size={16} className="text-amber-200 animate-spin shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-[13px] text-amber-50 font-semibold leading-snug">
                                    {us.phase === "uploading"
                                        ? `Uploading ${us.filename}…`
                                        : "Processing script… this may take a moment."}
                                </p>
                                <p className="text-[11px] text-amber-100/80 leading-relaxed mt-1">
                                    {us.phase === "uploading"
                                        ? "Please keep this page open until we confirm the revision is ready."
                                        : "We\u2019re still working on it. The page will update automatically when the revision is ready for review — no need to refresh."}
                                </p>
                            </div>
                        </div>
                    );
                }
                if (us.phase === "ready") {
                    return (
                        <div
                            data-testid={`script-revision-ready-banner-${scriptId}`}
                            data-phase="ready"
                            className="rounded-lg border border-emerald-400/50 bg-emerald-500/10 p-3 flex items-start gap-3"
                        >
                            <CheckCircle2 size={16} className="text-emerald-300 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-[13px] text-emerald-50 font-semibold leading-snug">
                                    {isPages
                                        ? `Revised pages ready for review`
                                        : `Revision ready for review`}
                                </p>
                                <p className="text-[11px] text-emerald-100/85 leading-relaxed mt-1">
                                    Open the revision below to see what changed and decide what to apply.
                                </p>
                                <div className="mt-2 flex items-center gap-2">
                                    {us.revision_id ? (
                                        <Button
                                            type="button"
                                            data-testid={`script-revision-ready-review-${scriptId}`}
                                            size="sm"
                                            onClick={() => {
                                                const rev = revisions.find(
                                                    (r) => r.id === us.revision_id,
                                                );
                                                setOpenRevId(us.revision_id);
                                                setOpenRevLabel(rev?.revision_label || us.filename);
                                                setOpenRevKind(
                                                    rev?.revision_kind || us.kind,
                                                );
                                            }}
                                            className="h-8 px-3 text-xs"
                                        >
                                            Review Now
                                        </Button>
                                    ) : null}
                                    <Button
                                        type="button"
                                        data-testid={`script-revision-ready-close-${scriptId}`}
                                        variant="ghost"
                                        size="sm"
                                        onClick={dismissUploadBanner}
                                        className="h-8 px-2 text-xs text-emerald-100/80 hover:text-emerald-50"
                                    >
                                        Later
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                }
                if (us.phase === "failed") {
                    return (
                        <div
                            data-testid={`script-revision-failed-banner-${scriptId}`}
                            data-phase="failed"
                            className="rounded-lg border border-red-400/50 bg-red-500/10 p-3 flex items-start gap-3"
                        >
                            <AlertTriangle size={16} className="text-red-300 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-[13px] text-red-50 font-semibold leading-snug">
                                    We couldn&rsquo;t process this revision
                                </p>
                                <p className="text-[11px] text-red-100/85 leading-relaxed mt-1">
                                    {us.error}
                                </p>
                                <p className="text-[11px] text-emerald-200/90 leading-relaxed mt-1">
                                    Your project is safe. Nothing has been changed.
                                </p>
                                <div className="mt-2">
                                    <Button
                                        type="button"
                                        data-testid={`script-revision-failed-close-${scriptId}`}
                                        variant="ghost"
                                        size="sm"
                                        onClick={dismissUploadBanner}
                                        className="h-8 px-3 text-xs text-red-100/85 hover:text-red-50"
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                }
                return null;
            })() : null}

            {loading ? (
                <div className="h-12 rounded-md bg-white/5 animate-pulse" />
            ) : revisions.length === 0 ? (
                <p className="text-[11px] text-neutral-500 italic leading-relaxed">
                    No script changes yet. Upload a few changed pages or a
                    new full script (PDF/DOCX) to start.
                </p>
            ) : (
                <ul
                    data-testid={`script-revision-list-${scriptId}`}
                    className="space-y-1.5"
                >
                    {revisions.map((r) => {
                        const isPages = r.revision_kind === "revised_pages";
                        const status = (r.status || "pending_revision").toLowerCase();
                        const isPending = status === "pending_revision";
                        const isApplied = status === "applied_revision";
                        const isArchived = status === "archived_by_revision";
                        return (
                            <li
                                key={r.id}
                                data-testid={`script-revision-row-${r.id}`}
                                data-status={status}
                                className={`flex items-center gap-2 rounded-md border px-2.5 py-2 ${
                                    isApplied
                                        ? "bg-emerald-500/5 border-emerald-500/20"
                                        : isArchived
                                            ? "bg-neutral-800/40 border-white/5 opacity-80"
                                            : "bg-card/60 border-white/10"
                                }`}
                            >
                                {isPages ? (
                                    <Layers
                                        size={12}
                                        className="text-emerald-300 shrink-0"
                                        data-testid={`script-revision-row-icon-pages-${r.id}`}
                                    />
                                ) : (
                                    <FileText
                                        size={12}
                                        className="text-amber-300 shrink-0"
                                        data-testid={`script-revision-row-icon-full-${r.id}`}
                                    />
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm text-white truncate flex items-center gap-1.5 flex-wrap">
                                        {r.revision_label || "Revision"}
                                        <span
                                            data-testid={`script-revision-row-kind-${r.id}`}
                                            className={`text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full font-semibold ${
                                                isPages
                                                    ? "bg-emerald-500/15 text-emerald-200"
                                                    : "bg-amber-500/15 text-amber-200"
                                            }`}
                                        >
                                            {isPages ? "Pages" : "Full script"}
                                        </span>
                                        {isApplied ? (
                                            <span
                                                data-testid={`script-revision-row-applied-${r.id}`}
                                                className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full font-semibold bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 flex items-center gap-1"
                                                title={r.applied_at ? `Applied ${new Date(r.applied_at).toLocaleString()}` : "Applied"}
                                            >
                                                <CheckCircle2 size={10} />
                                                Applied
                                            </span>
                                        ) : isArchived ? (
                                            <span
                                                data-testid={`script-revision-row-archived-${r.id}`}
                                                className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full font-semibold bg-neutral-500/15 text-neutral-300 border border-white/10"
                                            >
                                                Archived
                                            </span>
                                        ) : (
                                            <span
                                                data-testid={`script-revision-row-pending-${r.id}`}
                                                className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full font-semibold bg-amber-500/15 text-amber-200 border border-amber-500/30"
                                            >
                                                Pending
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-[10px] text-neutral-500 truncate">
                                        {r.filename || "—"}
                                    </p>
                                </div>
                                {isPending ? (
                                    <>
                                        <Button
                                            type="button"
                                            data-testid={`script-revision-delete-${r.id}`}
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setConfirmDeleteRev(r)}
                                            aria-label="Remove this revision"
                                            className="h-8 w-8 p-0 text-neutral-400 hover:text-red-300 hover:bg-red-500/10"
                                        >
                                            <Trash2 size={13} />
                                        </Button>
                                        <Button
                                            type="button"
                                            data-testid={`script-revision-review-${r.id}`}
                                            size="sm"
                                            onClick={() => {
                                                setOpenRevId(r.id);
                                                setOpenRevLabel(r.revision_label || "");
                                                setOpenRevKind(r.revision_kind || "full_script");
                                            }}
                                            className="h-8 px-2.5 text-xs font-semibold"
                                        >
                                            Review
                                        </Button>
                                    </>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            )}

            <ScriptRevisionKindPicker
                open={kindPickerOpen}
                onOpenChange={(v) => {
                    setKindPickerOpen(v);
                    if (!v) setPendingKind(null);
                }}
                onPick={onKindPicked}
            />

            <ScriptRevisionDiffDialog
                projectId={projectId}
                revisionId={openRevId}
                revisionLabel={openRevLabel}
                revisionKind={openRevKind}
                open={Boolean(openRevId)}
                onOpenChange={(v) => {
                    if (!v) {
                        setOpenRevId(null);
                        setOpenRevLabel("");
                        setOpenRevKind(null);
                    }
                }}
                onApproved={() => {
                    // Iter-157P.18.1 — after a successful Apply,
                    // the "Revision ready for review" banner above
                    // becomes misleading: hitting Review Now then
                    // produces "Diff only applies to pending
                    // revisions" because the revision is now active.
                    // Clear the banner state in the same tick so
                    // the panel reflects the new reality
                    // immediately, without a page refresh.
                    pollAbortRef.current.cancelled = true;
                    setUploadState(null);
                    refresh();
                }}
            />

            <Dialog
                open={Boolean(confirmDeleteRev)}
                onOpenChange={(v) => {
                    if (!v && !deleting) setConfirmDeleteRev(null);
                }}
            >
                <DialogContent
                    data-testid="script-revision-delete-dialog"
                    className="max-w-md bg-card border-red-500/30"
                >
                    <DialogHeader className="pr-8">
                        <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                            <AlertTriangle size={18} className="text-red-300" />
                            Remove this revision?
                        </DialogTitle>
                        <DialogDescription className="text-neutral-300">
                            Removes the pending revision&nbsp;
                            <span className="text-white font-mono text-xs bg-secondary px-1.5 py-0.5 rounded">
                                {confirmDeleteRev?.revision_label || "Revision"}
                            </span>
                            &nbsp;before approval. The current active script,
                            scenes, photos, notes and looks are not affected.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex gap-2 pt-4">
                        <Button
                            type="button"
                            data-testid="script-revision-delete-cancel"
                            variant="secondary"
                            onClick={() => setConfirmDeleteRev(null)}
                            disabled={deleting}
                            className="flex-1 min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            data-testid="script-revision-delete-confirm"
                            variant="destructive"
                            onClick={() => deleteRevision(confirmDeleteRev)}
                            disabled={deleting}
                            className="flex-1 min-tap font-semibold"
                        >
                            {deleting ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <>
                                    <Trash2 size={14} className="mr-1.5" />
                                    Remove revision
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
